<template>
  <div class="paradise-exclusive-container" v-if="galleryItems?.length">
    <div class="paradise-exclusive-title">
      <div class="title-text">
        <span class="heading3"
          >{{ $t('paradise') }}&nbsp;<span class="heading3-italic">{{
            $t('exclusive')
          }}</span></span
        >
      </div>
      <div class="title-action">
        <ElButton type="link arrow" @click="$emit('allExperiences')"
          >{{ $t('all') }}<Icons name="16px/arrow_right_white"
        /></ElButton>
      </div>
    </div>
    <div class="paradise-exclusive-gallery">
      <div class="gallery-container">
        <carousel
          :items-to-scroll="width <= 1100 ? 1 : 1"
          :breakpoints="{
            0: {
              itemsToShow: 1,
              snapAlign: 'start'
            },
            700: {
              itemsToShow: 2,
              snapAlign: 'start'
            },
            1200: {
              itemsToShow: 2,
              snapAlign: 'start'
            },
            1450: {
              itemsToShow: 2.2,
              snapAlign: 'start'
            },
            1600: {
              itemsToShow: 2.2,
              snapAlign: 'start'
            },
            1900: {
              itemsToShow: 2.2,
              snapAlign: 'start'
            }
          }"
        >
          <slide
            v-for="item in galleryItems"
            :key="item.uuid"
            @click="router.push(`/experiences/${item.uuid}`)"
          >
            <div class="gallery">
              <div
                class="image"
                :class="item.cover || item.image ? 'with-image' : ''"
              >
                <div class="sold-out" v-if="item.remaining_uses <= 0">
                  <Icons name="16px/lock" />
                  <span class="small">
                    {{ $t('sold_out') }}
                  </span>
                </div>
                <div
                  class="exclusive"
                  v-if="getAuthUser()?.value?.uuid && !item.user_can_purchase"
                >
                  <span class="small">
                    {{ $t('exclusive') }}
                  </span>
                </div>
                <video
                  v-if="item.cover"
                  :src="item.cover"
                  autoplay
                  muted
                  loop
                />
                <img v-else-if="item.image" :src="item.image" loading="lazy" />
                <img
                  v-else
                  src="/logos/PALC_Marque_Gold.svg"
                  alt="logo"
                  class="logo-img"
                />
              </div>
              <div class="gallery-title">
                <span class="heading6">{{ item.title }}</span>
              </div>
              <div class="gallery-date">
                <span class="caption" v-if="item.location">{{
                  item.location
                }}</span>
                <span class="caption" v-if="item.date">{{ item.date }}</span>
              </div>
            </div>
          </slide>
          <template #addons>
            <navigation />
            <pagination />
          </template>
        </carousel>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment/dist/locale/es'
import 'moment/dist/locale/en-gb'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/de'
import 'moment/dist/locale/sv'
import 'moment/dist/locale/ar'
import { ElLoading, ElMessageBox } from 'element-plus'
import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'ParadiseExclusive',
  data() {
    return {
      experiences: null,
      loadingDialog: null
    }
  },
  components: {
    Carousel,
    Slide,
    Pagination,
    Navigation
  },
  setup() {
    const { width, height } = useBreakpoints()
    const { t } = useI18n()
    const { $sentry } = useNuxtApp()
    const router = useRouter()

    const initialVariables = []

    let objectToReturn = {
      width,
      height,
      t,
      router,
      $sentry
    }
    for (const variable of initialVariables) {
      objectToReturn = Object.assign(objectToReturn, variable)
    }

    return objectToReturn
  },
  computed: {
    language() {
      return getAuthUser()?.value?.language
        ? getAuthUser()?.value?.language
        : this.$i18n.locale
    },
    galleryItems() {
      if (this.experiences?.getAllExperiences?.data?.length) {
        return this.experiences.getAllExperiences.data.map((experience) => {
          return {
            uuid: experience.uuid,
            title:
              this.$i18n.locale === 'es'
                ? experience.name_es
                : this.$i18n.locale === 'en'
                ? experience.name_en
                : this.$i18n.locale === 'fr'
                ? experience.name_fr
                : this.$i18n.locale === 'de'
                ? experience.name_de
                : this.$i18n.locale === 'sv'
                ? experience.name_sv
                : this.$i18n.locale === 'ar'
                ? experience.name_ar
                : experience.name,
            cover:
              getAuthUser()?.value?.uuid && experience.coverVideo?.url
                ? experience.coverVideo?.url
                : null,
            image: experience.mainImage?.url ?? null,
            location: experience.location?.name ?? null,
            date: this.getExperienceDate(experience),
            remaining_uses: experience.remaining_uses ?? 0,
            user_can_purchase: experience.user_can_purchase,
            price: experience.price,
            currency: getAuthUser()?.value?.currency ?? 'usd'
          }
        })
      }
      return []
    }
  },
  mounted() {
    this.getExperiences()
  },
  methods: {
    async getExperiences(refetch = false) {
      try {
        this.loadingDialog = ElLoading.service({
          fullscreen: true,
          background: '#F7F7F7',
          svg: useLoaderSvg(),
          svgViewBox: useLoaderSvgViewBox()
        })
        this.experiences = await apolloExperiences(
          {
            sortBy: 'CREATED_AT',
            sortDesc: true,
            search: null,
            page: 1,
            first: 8
          },
          true,
          {
            where: {
              featured: {
                column: 'FEATURED',
                value: true
              }
            },
            filterField: {
              filterEnabled: {
                value: true
              }
            }
          },
          refetch ? 'network-only' : null
        )
      } catch (err) {
        this.$showError(err, this.t)
        this.$sentry(err, 'getExperiences')
      } finally {
        this.$emit('calculateOffset')
        this.loadingDialog.close()
      }
    },
    getExperienceDate(experience) {
      if (!experience.start_date || !experience.end_date) {
        return null
      }
      moment.locale(this.language)
      const startDate = moment(experience.start_date)
      const endDate = moment(experience.end_date)
      if (startDate.isSame(endDate, 'month')) {
        return ` / ${startDate.format('DD')} - ${endDate.format('DD MMMM')}`
      }
      return ` / ${startDate.format('DD MMMM')} -
              ${endDate.format('DD MMMM')}`
    }
  }
})
</script>

<style scoped lang="scss">
.paradise-exclusive-container {
  @media (min-width: 1100px) {
    padding: 80px 0 60px 0;
  }
  @media (max-width: 1100px) {
    padding: 80px 0 60px 0;
  }
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: left;
  background-color: var(--brand-off-black);
  .paradise-exclusive-title {
    width: 100%;
    text-align: left;
    margin-bottom: 24px;
    display: block;
    position: relative;
    @media (min-width: 1100px) {
      padding-left: 96px;
      padding-right: 96px;
    }
    @media (max-width: 1100px) {
      padding-left: 24px;
      padding-right: 24px;
    }
    .title-text {
      display: inline-block;
      .heading3 {
        color: var(--brand-white);
      }
    }
    .title-action {
      float: right;
      display: inline-block;
      position: relative;
      top: 6px;
      i {
        margin-left: 4px;
        fill: #ffffff !important;
        svg {
          fill: #ffffff !important;
        }
      }
    }
  }
  .paradise-exclusive-gallery {
    overflow-x: hidden;
    white-space: nowrap;
    text-align: left;
    width: 100%;
    max-width: 100vw;
    .gallery-container {
      @media (min-width: 1100px) {
        padding-left: 96px;
        padding-right: 96px;
      }
      @media (max-width: 1100px) {
        padding-left: 24px;
        padding-right: 24px;
      }
      .carousel__slide {
        @media (min-width: 1100px) {
          height: 540px !important;
        }
        @media (max-width: 1100px) {
          height: 465px !important;
        }
        align-items: flex-start;
        padding-right: 16px;
      }
      .gallery {
        cursor: pointer;
        text-align: left;
        width: 100%;
        @media (min-width: 1100px) {
          height: 540px;
        }
        @media (max-width: 1100px) {
          height: 465px;
        }
        display: inline-block;
        vertical-align: top;
        .image {
          @media (min-width: 1100px) {
            height: 400px;
          }
          @media (max-width: 1100px) {
            height: 327px;
          }
          .sold-out {
            display: flex;
            height: 32px;
            padding: 8px 12px;
            position: absolute;
            left: 8px;
            top: 8px;
            border-radius: 16px;
            background: var(--brand-white);
            i {
              margin-right: 4px;
            }
          }
          .exclusive {
            display: flex;
            height: 32px;
            padding: 8px 12px;
            position: absolute;
            right: 24px;
            top: 8px;
            border-radius: 16px;
            background: var(--tins-light-2);
          }
          &.with-image {
            img,
            video {
              object-fit: cover;
              border-radius: 3px;
              width: 100%;
              height: 100%;
            }
          }
          &:not(.with-image) {
            @media (min-width: 1100px) {
              height: 400px;
            }
            @media (max-width: 1100px) {
              height: 327px;
            }
            background-color: var(--tins-light-3);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 3px;
            img {
              height: 120px;
              width: 171px;
            }
          }
        }
        .gallery-title {
          margin-top: 12px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          .heading6 {
            color: var(--brand-white);
          }
        }
        .gallery-date {
          margin-top: 8px;
          height: 16px;
          .caption {
            color: var(--brand-white);
          }
        }
        .gallery-price {
          margin-top: 4px;
          height: 16px;
          .caption {
            color: var(--brand-white);
          }
        }
      }
    }
  }
}
</style>